<div class="contract-view-container">

  @if (loading) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading contract...</p>
    </div>
  }

  @if (error) {
    <mat-card class="error-card">
      <mat-card-content>
        <mat-icon color="warn">error</mat-icon>
        <h2>{{ error }}</h2>
        <button mat-raised-button color="primary" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Return to Contracts
        </button>
      </mat-card-content>
    </mat-card>
  }

  @if (!loading && !error && currentContract) {

    <mat-sidenav-container class="sidenav-container">

      <!-- Actions Sidenav (Left Side) -->
      <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          title="Contract Actions"
          [actions]="sidenavActions"
          [tabs]="sidenavTabs"
          [selectedTabIndex]="selectedTabIndex"
          (tabSelected)="selectTab($event)">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <!-- Main Content -->
      <mat-sidenav-content class="form-content" [class.with-viewer]="showDocumentViewer">

        <!-- Archived Version Banner -->
        @if (currentContract.activeContract === false) {
          <div class="archived-banner">
            <mat-icon>archive</mat-icon>
            <span>Archived Version — Superseded on {{ currentContract.updatedAt | date:'MM/dd/yyyy' }}</span>
          </div>
        }

        <!-- Header Actions Bar -->
        <div class="header-bar">
          <button mat-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            Back to Contracts
          </button>

          <div class="header-actions">
            @if (currentDocument) {
              <button mat-stroked-button (click)="toggleDocumentViewer()">
                <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                {{ showDocumentViewer ? 'Hide' : 'View' }} Document
              </button>
              <button mat-stroked-button (click)="downloadDocument()">
                <mat-icon>download</mat-icon>
                Download
              </button>
              <button mat-stroked-button
                      color="accent"
                      (click)="openAIAnalysis()"
                      [disabled]="!isPdfDocument() || aiAnalysisLoading"
                      [matTooltip]="!isPdfDocument() ? 'AI Analysis is only available for PDF documents' : (aiAnalysisLoading ? aiAnalysisStatusMessage : '')">
                @if (aiAnalysisLoading) {
                  <mat-spinner diameter="18" class="button-spinner"></mat-spinner>
                } @else {
                  <mat-icon>psychology</mat-icon>
                }
                {{ aiAnalysisLoading ? aiAnalysisStatusMessage : 'AI Analysis' }}
              </button>
              <button mat-stroked-button
                      color="accent"
                      (click)="openAIAnalysis(true)"
                      [disabled]="!isPdfDocument() || aiAnalysisLoading"
                      matTooltip="Bypass the cached result and re-run the AI (picks up newly-added clause types)">
                <mat-icon>refresh</mat-icon>
                Re-analyze
              </button>
            }
            <button mat-stroked-button (click)="selectTab(1)">
              <mat-icon>link</mat-icon>
              Associated Contracts
            </button>
            <button mat-raised-button color="primary" (click)="editContract()">
              <mat-icon>edit</mat-icon>
              Edit Contract
            </button>
          </div>
        </div>

        <!-- Boolean Indicators - Only show row if at least one is true -->
        @if (currentContract.embeddedBaa || currentContract.baaRequired || currentContract.coiRequired || currentContract.poRequired || currentContract.missingSignature || currentContract.masterServicesAgreement || currentContract.subordinateAgreement) {
          <div class="compliance-indicators">
            <mat-chip-set>
              @if (currentContract.masterServicesAgreement) {
                <mat-chip highlighted class="compact-chip">
                  Master Services Agreement
                </mat-chip>
              }
              @if (currentContract.subordinateAgreement) {
                <mat-chip highlighted class="compact-chip">
                  Subordinate Agreement
                </mat-chip>
              }
              @if (currentContract.embeddedBaa) {
                <mat-chip highlighted class="compact-chip">
                  Embedded BAA
                </mat-chip>
              }
              @if (currentContract.baaRequired) {
                <mat-chip highlighted class="compact-chip">
                  Missing BAA
                </mat-chip>
              }
              @if (currentContract.coiRequired) {
                <mat-chip highlighted class="compact-chip">
                  Missing COI
                </mat-chip>
              }
              @if (currentContract.poRequired) {
                <mat-chip highlighted class="compact-chip">
                  Missing PO
                </mat-chip>
              }
              @if (currentContract.missingSignature) {
                <mat-chip highlighted class="compact-chip">
                  Missing Signature
                </mat-chip>
              }
              @if (currentContract.autorenew) {
                <mat-chip highlighted class="compact-chip">
                  Auto-Renew Contract: ON
                </mat-chip>
              }
            </mat-chip-set>
          </div>
        }

        <!-- Static Information Section (Always Visible) -->
        <div class="static-info-section">
          <!-- Contract Name Header -->
          <div class="contract-name-header">
            <h1>{{ currentContract.nameOfContract }}</h1>
            <div class="contract-metadata">
              <span>Contract ID: {{ currentContract.contractId || '' }}</span>
              @if (currentContract.clientContractId) {
                <span class="separator">•</span>
                <span>Client Contract Num: {{ currentContract.clientContractId }}</span>
              }
            </div>
            @if (currentDocument) {
              <div class="document-preview">
                <mat-icon color="primary">description</mat-icon>
                <span class="main-document-label">Main Document</span>
                <span class="file-type-badge">{{ currentDocument.fileType.toUpperCase() }}</span>
                <button mat-icon-button
                        [color]="showDocumentViewer ? 'primary' : ''"
                        (click)="toggleDocumentViewer()"
                        matTooltip="Toggle contract viewer">
                  <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <div class="version-info">
                  <span class="version-label">Version</span>
                  @if (!currentContract.versionList || currentContract.versionList.length === 0) {
                    <span class="version-value">1 of 1</span>
                  } @else {
                    <span class="version-value">{{ currentContract.version || (currentContract.versionList.indexOf(currentContract.contractNum) + 1) || 1 }} of {{ currentContract.versionList.length }}</span>
                  }
                </div>
                <button mat-stroked-button
                        (click)="selectTab(6)"
                        class="compact-button">
                  Contract History
                </button>
                <button mat-stroked-button
                        [disabled]="!currentContract.description"
                        (click)="openDescriptionDialog()"
                        class="compact-button">
                  Description
                </button>
                <button mat-stroked-button
                        [disabled]="!currentContract.termProvision"
                        (click)="openTermProvisionDialog()"
                        class="compact-button">
                  Initlal/Amended Term
                </button>
              </div>
            }
          </div>

          <!-- SANDBOX-ONLY: what the nightly contract job will do. reminderTestTools is
               set in environment.sandbox-ng.ts only; the backend also refuses other orgs. -->
          @if (showContractPlan && currentContract.contractId) {
            <app-contract-plan-panel [contractId]="currentContract.contractId"></app-contract-plan-panel>
          }

          <!-- Essential Information -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>business</mat-icon>
              <h3>Essential Information</h3>
            </div>
            <div class="info-grid essential-grid">
              <div class="info-field">
                <label>Main Document Name</label>
                <div class="value">{{ currentContract.mainDocumentName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Status</label>
                <div class="value">
                  <mat-chip-set>
                    <mat-chip [highlighted]="currentContract.status === 'Active'">
                      {{ currentContract.status || '' }}
                    </mat-chip>
                  </mat-chip-set>
                </div>
              </div>

              <div class="info-field">
                <label>Contract Type</label>
                <div class="value">{{ currentContract.contractType || '' }}</div>
              </div>

              <div class="info-field">
                <label>Database Organization</label>
                <div class="value">{{ currentContract.databaseOrg || '' }}</div>
              </div>

              <div class="info-field">
                <label>Your Legal Entity</label>
                <div class="value">{{ legalEntitiesDisplay || '' }}</div>
              </div>

              <div class="info-field">
                <label>Your Sites</label>
                <div class="value">{{ sitesDisplay || '' }}</div>
              </div>

              <div class="info-field">
                <label>Your Departments</label>
                <div class="value">{{ (currentContract.departments && currentContract.departments.length > 0) ? currentContract.departments.join(', ') : '' }}</div>
              </div>
            </div>
          </div>

          <!-- Timeline & Terms -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>schedule</mat-icon>
              <h3>Timeline & Terms</h3>
            </div>
            <div class="info-grid terms-grid">
              <div class="info-field">
                <label>Effective Date</label>
                <div class="value">{{ formatDate(currentContract.effectiveDate) || '' }}</div>
              </div>

              <div class="info-field">
                <label>Commencement Date (if applicable)</label>
                <div class="value">{{ formatDate(currentContract.executionDate) || '' }}</div>
              </div>

              <div class="info-field">
                <label>Initial/Amended Term</label>
                <div class="value">{{ currentContract.initialTerm ? currentContract.initialTerm + ' months' : '' }}</div>
              </div>

              <div class="info-field">
                <label>Expiration Date</label>
                <div class="value">{{ formatDate(currentContract.expirationDate) || '' }}</div>
              </div>

              <div class="info-field">
                <label>Renewal Type</label>
                <div class="value">{{ currentContract.renewalType || '' }}</div>
              </div>

              <div class="info-field">
                <label>Renewal Term</label>
                <div class="value">{{ currentContract.renewalTerm ? currentContract.renewalTerm + ' months' : '' }}</div>
              </div>

              <div class="info-field">
                <label>Contractual Notice Period</label>
                <div class="value">{{ currentContract.noticePeriod ? currentContract.noticePeriod + ' days' : '' }}</div>
              </div>

              <div class="info-field">
                <label>Additional Review Period</label>
                <div class="value">{{ currentContract.reviewPeriod ? currentContract.reviewPeriod + ' days' : '' }}</div>
              </div>

              <div class="info-field">
                <label>Last Review</label>
                <div class="value">{{ formatDate(currentContract.lastReviewDate) || '' }}</div>
              </div>

              <div class="info-field">
                <label>Next Review</label>
                <div class="value">{{ formatDate(currentContract.nextReviewDate) || '' }}</div>
              </div>

              <div class="info-field">
                <label>Renewal Cycle Limit</label>
                <div class="value">{{ currentContract.renewalCycleLimit || '' }}</div>
              </div>

              <div class="info-field">
                <label>Renewals Completed</label>
                <div class="value">{{ currentContract.numRenewals || '' }}</div>
              </div>
            </div>
          </div>

        </div>

        <!-- Tabbed Content -->
        <mat-tab-group
          [selectedIndex]="selectedTabIndex"
          (selectedIndexChange)="selectTab($event)"
          class="contract-tabs">

          <!-- Tab 0: Details -->
          <mat-tab label="Details">
            <div class="tab-content">

              <!-- Party Details Card -->
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>business</mat-icon>
                    Party Details
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="party-details-grid">

                    <div class="info-field">
                      <label>Party</label>
                      <div class="value">
                        @if (directVendorUrl) {
                          <a [href]="directVendorUrl" target="_blank" class="entity-link">{{ directVendorName }}</a>
                        } @else {
                          {{ directVendorName || 'N/A' }}
                        }
                      </div>
                    </div>

                    <div class="info-field">
                      <label>Parent Party</label>
                      <div class="value">
                        @if (parentVendorUrl) {
                          <a [href]="parentVendorUrl" target="_blank" class="entity-link">{{ parentVendorName }}</a>
                        } @else {
                          {{ parentVendorName || 'N/A' }}
                        }
                      </div>
                    </div>

                    <div class="info-field">
                      <label>Party Signatory</label>
                      <div class="value">{{ partiesSignatoryName || 'N/A' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Party Signatory Date</label>
                      <div class="value">{{ formatDate(currentContract.externalExecutionDate) || 'N/A' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Party Contract Coordinator</label>
                      <div class="value">{{ partyCoordinatorName || 'N/A' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Other Parties</label>
                      <div class="value">
                        @if (otherPartiesUrls.length > 0) {
                          <div class="entity-links-list">
                            @for (party of otherPartiesUrls; track party.url) {
                              <a [href]="party.url" target="_blank" class="entity-link">{{ party.name }}</a>
                            }
                          </div>
                        } @else {
                          {{ otherPartiesDisplay || 'N/A' }}
                        }
                      </div>
                    </div>

                    <div class="info-field" style="grid-column: 1 / -1;">
                      <label>Sub-Contractors</label>
                      <div class="value">
                        @if (subcontractorsUrls.length > 0) {
                          <div class="entity-links-list">
                            @for (subcontractor of subcontractorsUrls; track subcontractor.url) {
                              <a [href]="subcontractor.url" target="_blank" class="entity-link">{{ subcontractor.name }}</a>
                            }
                          </div>
                        } @else {
                          {{ subcontractorsDisplay || 'N/A' }}
                        }
                      </div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>

              <!-- Internal Details Card -->
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>person</mat-icon>
                    Internal Details
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Primary Owner</label>
                      <div class="value">{{ primaryOwnerName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Secondary Owner</label>
                      <div class="value">{{ secondaryOwnerName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Tertiary Owner</label>
                      <div class="value">{{ tertiaryOwnerName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Internal Signatory</label>
                      <div class="value">{{ internalSignatoryName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Internal Signatory Date</label>
                      <div class="value">{{ formatDate(currentContract.internalExecutionDate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Internal Coordinator</label>
                      <div class="value">{{ contractCoordinatorName || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>

              <!-- Basic Financial Information Card -->
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>account_balance_wallet</mat-icon>
                    Basic Financial Information
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Total Value</label>
                      <div class="value">{{ formatCurrency(currentContract.totalValue) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Annual Value</label>
                      <div class="value">{{ formatCurrency(currentContract.annualValue) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Monthly Value</label>
                      <div class="value">{{ formatCurrency(currentContract.monthlyValue) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Purchase Price</label>
                      <div class="value">{{ formatCurrency(currentContract.purchasePrice) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>PO Number</label>
                      <div class="value">{{ currentContract.poNumber || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Payment Terms</label>
                      <div class="value">{{ currentContract.paymentTerms || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Cost Center</label>
                      <div class="value">{{ currentContract.costCenter || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>

              <!-- Compliance and Risk Details Card -->
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>security</mat-icon>
                    Compliance and Risk Details
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Risk Category</label>
                      <div class="value">{{ currentContract.riskCategory || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Overall Risk Score</label>
                      <div class="value">{{ currentContract.riskScore || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Regulatory</label>
                      <div class="value">{{ (currentContract.regulatory && currentContract.regulatory.length > 0) ? currentContract.regulatory.join(', ') : '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Clinical or Admin</label>
                      <div class="value">{{ currentContract.clinicalOrAdmin || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Contract Label</label>
                      <div class="value">{{ currentContract.contractLabel || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>

            </div>
          </mat-tab>

          <!-- Tab 1: Associated Contracts -->
          <mat-tab label="Associated Contracts">
            <div class="tab-content">
              <!-- Master Contract Section -->
              @if (currentContract.masterContract) {
                <mat-card>
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>link</mat-icon>
                      Master Contract
                    </mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <div class="info-grid">
                      <div class="info-field full-width">
                        <label>Master Contract</label>
                        <div class="value">
                          @if (masterContractUrl) {
                            <a [href]="masterContractUrl" target="_blank" class="entity-link">
                              {{ currentContract.masterContract?.nameOfContract || currentContract.masterContract }}
                            </a>
                          } @else {
                            {{ currentContract.masterContract?.nameOfContract || currentContract.masterContract }}
                          }
                        </div>
                      </div>
                    </div>
                  </mat-card-content>
                </mat-card>
              }

              <!-- Associated Contracts Section (contracts sharing the same primary party) -->
              <app-contract-connections
                [selectedContractList]="currentContract.associatedContracts || []">
              </app-contract-connections>
            </div>
          </mat-tab>

          <!-- Tab 2: BAAs -->
          <mat-tab label="BAAs">
            <div class="tab-content">
              @if (currentContract.directBaa) {
                <app-baa-detail-inline
                  [baaNum]="currentContract.directBaa">
                </app-baa-detail-inline>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>policy</mat-icon>
                    <p>No Business Associate Agreement associated with this contract</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 3: COIs -->
          <mat-tab label="COIs">
            <div class="tab-content">
              @if (currentContract.directCoi) {
                <app-coi-detail-inline
                  [coiNum]="currentContract.directCoi">
                </app-coi-detail-inline>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>verified_user</mat-icon>
                    <p>No Certificate of Insurance associated with this contract</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 4: Attachments -->
          <mat-tab label="Attachments">
            <div class="tab-content">
              @if (entityAttachments && entityAttachments.length > 0) {
                <app-attachment-connections
                  [attachmentObjectArray]="entityAttachments"
                  (attachmentUpdated)="refreshAttachments()">
                </app-attachment-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>attach_file</mat-icon>
                    <p>No attachments for this contract</p>
                    <button mat-raised-button color="primary" (click)="addAttachment()">
                      <mat-icon>add</mat-icon>
                      Add Attachment
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 5: Important Dates -->
          <mat-tab label="Important Dates">
            <div class="tab-content">
              @if (entityDates && entityDates.length > 0) {
                <app-critical-date-connections
                  [dateObjectArray]="entityDates"
                  (criticalDateUpdated)="refreshCriticalDates()">
                </app-critical-date-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>event</mat-icon>
                    <p>No important dates for this contract</p>
                    <button mat-raised-button color="primary" (click)="addCriticalDate()">
                      <mat-icon>add</mat-icon>
                      Add Important Date
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Hidden - People tab removed from view -->


          <!-- Tab 7: Contract History (Versions) -->
          <mat-tab label="Contract History">
            <div class="tab-content">
              <app-versions
                entityType="contract"
                [versionList]="currentContract.versionList || []"
                [currentEntityNum]="currentContract.contractNum || ''">
              </app-versions>
            </div>
          </mat-tab>

          <!-- Tab 8: Provisions (Key Contract Sections) -->
          <mat-tab label="Provisions">
            <div class="tab-content">
              @if (hasAnyKeySections()) {
                <mat-card class="provisions-card">
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>gavel</mat-icon>
                      Key Contract Sections
                    </mat-card-title>
                    <span class="provisions-subtitle">Extracted contract language for review and risk analysis</span>
                  </mat-card-header>
                  <mat-card-content>
                    <mat-accordion class="provisions-accordion">
                      @for (section of getKeySectionsArray(); track section.key) {
                        <mat-expansion-panel [disabled]="!section.content">
                          <mat-expansion-panel-header>
                            <mat-panel-title>
                              @if (section.content) {
                                <mat-icon class="section-found">check_circle</mat-icon>
                              } @else {
                                <mat-icon class="section-not-found">cancel</mat-icon>
                              }
                              {{ section.label }}
                            </mat-panel-title>
                            <mat-panel-description>
                              @if (!section.content) {
                                <span class="not-found-text">Not extracted</span>
                              }
                            </mat-panel-description>
                          </mat-expansion-panel-header>
                          @if (section.content) {
                            <div class="section-content">
                              <p class="section-language">"{{ section.content }}"</p>
                            </div>
                          }
                        </mat-expansion-panel>
                      }
                    </mat-accordion>
                  </mat-card-content>
                </mat-card>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>gavel</mat-icon>
                    <p>No contract provisions have been extracted yet.</p>
                    <p class="empty-state-hint">Use "Process with AI" when creating or editing this contract to automatically extract key sections.</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 9: Notes -->
          <mat-tab label="Notes">
            <div class="tab-content">
              <app-notes
                [notes]="currentContract?.notes || []"
                (notesSaved)="saveNotes($event)">
              </app-notes>
            </div>
          </mat-tab>

          <!-- Tab 10: HCP Benefits -->
          <mat-tab label="HCP Benefits">
            <div class="tab-content">
              <mat-card class="empty-state">
                <mat-card-content>
                  <mat-icon>local_hospital</mat-icon>
                  <p>HCP Benefits content coming soon</p>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>

          <!-- Tab 11: Data Tables -->
          <mat-tab label="Data Tables">
            <div class="tab-content">
              <mat-card class="empty-state">
                <mat-card-content>
                  <mat-icon>table_chart</mat-icon>
                  <p>Data Tables content coming soon</p>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>

          <!-- Tab 12: Assessments -->
          <mat-tab label="Assessments">
            <div class="tab-content">
              <app-assessment-connections
                [entityId]="currentContract.contractId || ''"
                entityType="Contract"
                [sourceRecord]="currentContract.friendlyName || currentContract.nameOfContract || ''">
              </app-assessment-connections>
            </div>
          </mat-tab>

          <!-- Hidden - Workflow tab commented out, backend not ready yet. Uncomment when workflow API is hooked up.
          <mat-tab label="Workflow">
            <div class="tab-content">
              <app-workflow-connections
                [entityId]="currentContract.contractId || ''"
                entityType="Contract"
                [sourceRecord]="currentContract.friendlyName || currentContract.nameOfContract || ''">
              </app-workflow-connections>
            </div>
          </mat-tab>
          -->

          <!-- Tab 14: Enable Timesheets (org-gated) -->
          @if (environment.timesheets) {
          <mat-tab label="Timesheets">
            <div class="tab-content">

              @if (timesheetCreating) {
                <div class="timesheet-loading">
                  <mat-spinner diameter="48"></mat-spinner>
                  <p>Creating timesheet specification...</p>
                </div>
              } @else if (!currentContract?.timesheetsEnabled && !linkedTimesheetSpec) {
                <!-- Not enabled — show big toggle -->
                <div class="timesheet-enable-container">
                  <mat-icon class="timesheet-hero-icon">schedule</mat-icon>
                  <h2 class="timesheet-hero-title">Timesheet Tracking</h2>
                  <p class="timesheet-hero-description">
                    Enable timesheet tracking for this contract to manage provider time reporting,
                    activity logging, and compensation approvals.
                  </p>
                  <div class="timesheet-toggle-area">
                    <mat-slide-toggle
                      [checked]="false"
                      (change)="enableTimesheets()"
                      color="primary"
                      class="timesheet-toggle">
                      <span class="toggle-label">Enable Timesheets for This Contract</span>
                    </mat-slide-toggle>
                  </div>
                  <p class="timesheet-warning">
                    <mat-icon>info</mat-icon>
                    Once enabled, a timesheet specification will be created and linked to this contract.
                    This action cannot be undone.
                  </p>
                </div>
              } @else {
                <!-- Enabled — show linked timesheet info -->
                <div class="timesheet-enabled-container">
                  <div class="timesheet-status-banner">
                    <mat-icon class="status-icon">check_circle</mat-icon>
                    <div class="status-text">
                      <h3>Timesheets Enabled</h3>
                      <p>This contract is linked to a timesheet specification.</p>
                    </div>
                  </div>

                  <!-- AI Analysis Status -->
                  @if (timesheetAiStatus() === 'analyzing') {
                    <div class="timesheet-ai-banner analyzing">
                      <mat-spinner diameter="20"></mat-spinner>
                      <span>Analyzing contract PDF for timesheet fields...</span>
                    </div>
                  }

                  @if (timesheetAiStatus() === 'done' && timesheetAiFieldCount > 0) {
                    <div class="timesheet-ai-banner success">
                      <mat-icon>auto_awesome</mat-icon>
                      <span>AI populated {{ timesheetAiFieldCount }} field{{ timesheetAiFieldCount > 1 ? 's' : '' }} from the contract</span>
                    </div>
                  }

                  <!-- AI Suggestions (suggested match — not auto-filled) -->
                  @if (timesheetAiSuggestions.length > 0) {
                    <div class="timesheet-suggestions">
                      <h4 class="suggestions-heading">
                        <mat-icon>auto_awesome</mat-icon>
                        AI Suggestions
                      </h4>
                      <p class="suggestions-subtext">These were found in the contract but we're not 100% sure. Review and accept or dismiss.</p>
                      @for (suggestion of timesheetAiSuggestions; track suggestion.field) {
                        <div class="suggestion-card">
                          <div class="suggestion-header">
                            <span class="suggestion-label">{{ suggestion.label }}</span>
                            <span class="suggestion-value">
                              @if (isArray(suggestion.value)) {
                                {{ suggestion.value.join(', ') }}
                              } @else {
                                {{ suggestion.value }}
                              }
                            </span>
                          </div>
                          <p class="suggestion-source">"{{ suggestion.sourceText }}"</p>
                          <div class="suggestion-actions">
                            <button mat-stroked-button color="primary" (click)="acceptAiSuggestion(suggestion)">
                              <mat-icon>check</mat-icon> Use This
                            </button>
                            <button mat-button (click)="dismissAiSuggestion(suggestion.field)">
                              Dismiss
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  }

                  @if (linkedTimesheetSpec && !linkedTimesheetSpec.userId) {
                    <div class="timesheet-warning-banner">
                      <mat-icon>warning</mat-icon>
                      <div>
                        <strong>No provider assigned</strong>
                        <p>This timesheet spec cannot be used until a doctor/provider is assigned to it. Edit the spec to assign a user.</p>
                      </div>
                    </div>
                  }

                  @if (linkedTimesheetSpec) {
                    <mat-card class="timesheet-info-card">
                      <mat-card-content>
                        <div class="info-row">
                          <span class="info-label">Timesheet ID</span>
                          <span class="info-value">{{ linkedTimesheetSpec.id || 'Pending' }}</span>
                        </div>
                        <div class="info-row">
                          <span class="info-label">Status</span>
                          <span class="info-value">{{ linkedTimesheetSpec.specState || 'Draft' }}</span>
                        </div>
                        <div class="info-row">
                          <span class="info-label">Start Date</span>
                          <span class="info-value">{{ linkedTimesheetSpec.startDate ? formatDate(linkedTimesheetSpec.startDate) : 'Not set' }}</span>
                        </div>
                        <div class="info-row">
                          <span class="info-label">End Date</span>
                          <span class="info-value">{{ linkedTimesheetSpec.endDate ? formatDate(linkedTimesheetSpec.endDate) : 'Not set' }}</span>
                        </div>
                      </mat-card-content>
                      <mat-card-actions>
                        <button mat-raised-button color="primary" (click)="openTimesheetSpec()">
                          <mat-icon>open_in_new</mat-icon> View Timesheet Spec
                        </button>
                        <button mat-stroked-button (click)="editTimesheetSpec()">
                          <mat-icon>edit</mat-icon> Edit Timesheet Spec
                        </button>
                      </mat-card-actions>
                    </mat-card>
                  } @else {
                    <p>Timesheet specification is being loaded...</p>
                  }
                </div>
              }

            </div>
          </mat-tab>
          }

          <!-- Tab 15: Grant Information -->
          @if (environment.grants) {
          <mat-tab label="Grant Information">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>account_balance</mat-icon>
                    Grant Information
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Grant Fund Number</label>
                      <div class="value">{{ currentContract.grantFundNumber || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Grant Document Number</label>
                      <div class="value">{{ currentContract.grantDocumentNumber || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Grant Program Key</label>
                      <div class="value">{{ currentContract.grantProgramKey || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Grant Use</label>
                      <div class="value">{{ currentContract.grantUse || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Grant Restrictions Type</label>
                      <div class="value">{{ currentContract.grantRestrictionsType || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Grant Program Number</label>
                      <div class="value">{{ currentContract.grantProgramNumber || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Revised Award Value</label>
                      <div class="value">{{ formatCurrency(currentContract.revisedAwardValue) || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          }

          <!-- Tab 16: Value Calculations -->
          @if (environment.value) {
          <mat-tab label="Value Calculations">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>calculate</mat-icon>
                    Value Calculations
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Value per Patient</label>
                      <div class="value">{{ formatCurrency(currentContract.valuePatient) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Hour</label>
                      <div class="value">{{ formatCurrency(currentContract.valueHour) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Day</label>
                      <div class="value">{{ formatCurrency(currentContract.valueDay) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Month</label>
                      <div class="value">{{ formatCurrency(currentContract.valueMonth) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Resident Per Year</label>
                      <div class="value">{{ formatCurrency(currentContract.valueResidentPerYear) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Diem</label>
                      <div class="value">{{ formatCurrency(currentContract.valuePerDiem) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Encounter</label>
                      <div class="value">{{ formatCurrency(currentContract.valueEncounter) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Delivery</label>
                      <div class="value">{{ formatCurrency(currentContract.valueDelivery) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Workshop</label>
                      <div class="value">{{ formatCurrency(currentContract.valueWorkshop) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Clinic</label>
                      <div class="value">{{ formatCurrency(currentContract.valueClinic) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Class</label>
                      <div class="value">{{ formatCurrency(currentContract.valueClass) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Event</label>
                      <div class="value">{{ formatCurrency(currentContract.valueEvent) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Program</label>
                      <div class="value">{{ formatCurrency(currentContract.valueProgram) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Student Per Week</label>
                      <div class="value">{{ formatCurrency(currentContract.valueStudentPerWeek) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Value per Student Per Month</label>
                      <div class="value">{{ formatCurrency(currentContract.valueStudentPerMonth) || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          }

          <!-- Tab 17: AP Compensation -->
          @if (environment.sochsAccordion) {
          <mat-tab label="AP Compensation">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>payments</mat-icon>
                    AP Compensation
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>AP Compensation Structure</label>
                      <div class="value">{{ currentContract.apCompensationStructure || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Acute Care Compensation</label>
                      <div class="value">{{ formatCurrency(currentContract.acuteCareComp) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Sign-On Bonus Amount</label>
                      <div class="value">{{ formatCurrency(currentContract.apSignOnBonusAmount) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Retention Bonus</label>
                      <div class="value">{{ formatCurrency(currentContract.apRetentionBonus) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Base Salary</label>
                      <div class="value">{{ formatCurrency(currentContract.apBaseSalary) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Hourly Rate</label>
                      <div class="value">{{ formatCurrency(currentContract.apHourlyRate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Productivity Bonus</label>
                      <div class="value">{{ formatCurrency(currentContract.apProductivityBonus) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Quality Bonus</label>
                      <div class="value">{{ formatCurrency(currentContract.apQualityBonus) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Shift Differential</label>
                      <div class="value">{{ formatCurrency(currentContract.apShiftDifferential) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Call Pay</label>
                      <div class="value">{{ formatCurrency(currentContract.apCallPay) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Malpractice Coverage</label>
                      <div class="value">{{ currentContract.apMalpracticeCoverage || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Relocation Assistance</label>
                      <div class="value">{{ formatCurrency(currentContract.apRelocationAssistance) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Student Loan Repayment</label>
                      <div class="value">{{ formatCurrency(currentContract.apStudentLoanRepayment) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Tail Coverage</label>
                      <div class="value">{{ currentContract.apTailCoverage || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Annual Bonus Potential</label>
                      <div class="value">{{ formatCurrency(currentContract.apAnnualBonusPotential) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Stipend</label>
                      <div class="value">{{ formatCurrency(currentContract.apStipend) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP RVU Rate</label>
                      <div class="value">{{ currentContract.apRvuRate || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Minimum Guarantee</label>
                      <div class="value">{{ formatCurrency(currentContract.apMinimumGuarantee) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Benefits Package Value</label>
                      <div class="value">{{ formatCurrency(currentContract.apBenefitsPackageValue) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Vacation Days</label>
                      <div class="value">{{ currentContract.apVacationDays || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP CME Allowance</label>
                      <div class="value">{{ formatCurrency(currentContract.apCmeAllowance) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Partnership Track</label>
                      <div class="value">{{ currentContract.apPartnershipTrack || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Non-Compete Radius</label>
                      <div class="value">{{ currentContract.apNonCompeteRadius || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Non-Compete Duration</label>
                      <div class="value">{{ currentContract.apNonCompeteDuration || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>AP Work Schedule</label>
                      <div class="value">{{ currentContract.apWorkSchedule || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          }

          <!-- Tab 18: HR Details -->
          @if (environment.hrAccordion) {
          <mat-tab label="HR Details">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>work</mat-icon>
                    HR Details
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>HR CME Dues</label>
                      <div class="value">{{ formatCurrency(currentContract.hrCmeDues) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR PTO Time Off</label>
                      <div class="value">{{ currentContract.hrPtoTimeOff || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Acute Care Specialty</label>
                      <div class="value">{{ currentContract.acuteCareSpecialty || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Group</label>
                      <div class="value">{{ currentContract.hrGroup || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Product</label>
                      <div class="value">{{ currentContract.hrProduct || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Lawson Number</label>
                      <div class="value">{{ currentContract.hrLawsonNumber || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Position Title</label>
                      <div class="value">{{ currentContract.hrPositionTitle || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Department Code</label>
                      <div class="value">{{ currentContract.hrDepartmentCode || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Job Code</label>
                      <div class="value">{{ currentContract.hrJobCode || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR FTE</label>
                      <div class="value">{{ currentContract.hrFte || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Supervisor Name</label>
                      <div class="value">{{ currentContract.hrSupervisorName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Employee ID</label>
                      <div class="value">{{ currentContract.hrEmployeeId || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Pay Grade</label>
                      <div class="value">{{ currentContract.hrPayGrade || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Pay Type</label>
                      <div class="value">{{ currentContract.hrPayType || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Benefits Eligible</label>
                      <div class="value">{{ currentContract.hrBenefitsEligible || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Start Date</label>
                      <div class="value">{{ formatDate(currentContract.hrStartDate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR End Date</label>
                      <div class="value">{{ formatDate(currentContract.hrEndDate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Location Code</label>
                      <div class="value">{{ currentContract.hrLocationCode || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Cost Center</label>
                      <div class="value">{{ currentContract.hrCostCenter || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>HR Union Status</label>
                      <div class="value">{{ currentContract.hrUnionStatus || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          }

          <!-- Tab 19: Lease Management -->
          @if (environment.leaseManagement) {
          <mat-tab label="Lease Management">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>home_work</mat-icon>
                    Lease Management
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <div class="info-grid additional-grid">

                    <div class="info-field">
                      <label>Lease Start Date</label>
                      <div class="value">{{ formatDate(currentContract.leaseStartDate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease End Date</label>
                      <div class="value">{{ formatDate(currentContract.leaseEndDate) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Monthly Rent</label>
                      <div class="value">{{ formatCurrency(currentContract.leaseMonthlyRent) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Annual Rent</label>
                      <div class="value">{{ formatCurrency(currentContract.leaseAnnualRent) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Square Footage</label>
                      <div class="value">{{ currentContract.leaseSquareFootage || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Price Per Square Foot</label>
                      <div class="value">{{ formatCurrency(currentContract.leasePricePerSqFt) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Security Deposit</label>
                      <div class="value">{{ formatCurrency(currentContract.leaseSecurityDeposit) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Escalation Clause</label>
                      <div class="value">{{ currentContract.leaseEscalationClause || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Escalation Percentage</label>
                      <div class="value">{{ currentContract.leaseEscalationPercentage || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Renewal Options</label>
                      <div class="value">{{ currentContract.leaseRenewalOptions || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Property Address</label>
                      <div class="value">{{ currentContract.leasePropertyAddress || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Type</label>
                      <div class="value">{{ currentContract.leaseType || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Landlord Name</label>
                      <div class="value">{{ currentContract.leaseLandlordName || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Operating Expenses</label>
                      <div class="value">{{ formatCurrency(currentContract.leaseOperatingExpenses) || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Parking Spaces</label>
                      <div class="value">{{ currentContract.leaseParkingSpaces || '' }}</div>
                    </div>

                    <div class="info-field">
                      <label>Lease Early Termination Clause</label>
                      <div class="value">{{ currentContract.leaseEarlyTerminationClause || '' }}</div>
                    </div>

                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          }

        </mat-tab-group>

      </mat-sidenav-content>

      <!-- Document Viewer - Using Reusable Component (Right Side) -->
      <mat-sidenav mode="side"
                  position="end"
                  [opened]="showDocumentViewer"
                  class="contract-viewer">
        <app-document-viewer-sidenav
          [document]="currentDocument"
          [title]="'Contract Viewer'"
          (close)="toggleDocumentViewer()"
          (download)="downloadDocument()"
          (openNewTab)="openDocumentInNewTab()">
        </app-document-viewer-sidenav>
      </mat-sidenav>

    </mat-sidenav-container>

  }

</div>
